import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, Grid, Stack, Switch, Typography } from '@mui/material';

import { fetchGet } from '../api/common';
import { StyledPaperItem } from '../common/mui-components';
import {
  SearchPoolData,
  SearchPoolStackedBar,
} from '../common/components/SearchPoolStackedBar';

type PropTypes = {
  close: () => void;
  open: boolean;
  profileId: string;
  username: string;
};

export const ViewSearchPoolDialog = ({
  close,
  open,
  profileId,
  username,
}: PropTypes) => {
  const [searchPoolData, setSearchPoolData] = useState<SearchPoolData>();
  const [totalMatches, setTotalMatches] = useState<number>();
  const [currentAllTimeToggle, setCurrentAllTimeToggle] = useState(false);
  const [selfLiveSeeds, setSelfLiveSeeds] = useState<number>();
  const [selfTotalSeeds, setSelfTotalSeeds] = useState<number>();

  useEffect(() => {
    if (!open) return;
    fetchGlobalSearchStats();
    fetchSelfSeedCounts();
  }, [open, currentAllTimeToggle]);

  const fetchGlobalSearchStats = async () => {
    const res = await fetchGet(
      `/match-making/global-search-stats/${currentAllTimeToggle}`
    );
    setSearchPoolData(res);
    setTotalMatches(
      res.matches1200 +
        res.matches12001500 +
        res.matches15001800 +
        res.matches1800
    );
  };

  const fetchSelfSeedCounts = async () => {
    const res = await fetchGet(`/match-making/seeded-games/${profileId}`);
    setSelfLiveSeeds(res.liveSeeds || 0);
    setSelfTotalSeeds(res.totalSeeds || 0);
  };

  return (
    <>
      <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" textAlign={'center'} letterSpacing={5}>
            SEEDED GAMES
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ width: '100%', height: '100%' }}>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              width: '100%',
              marginBottom: 2,
              color: 'dailies.main',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography>Current</Typography>
            <Switch
              checked={currentAllTimeToggle}
              onChange={() => setCurrentAllTimeToggle((prev) => !prev)}
            />
            <Typography>All Time</Typography>
          </Stack>
          {searchPoolData ? (
            <Box
              sx={{
                position: 'relative',
                flexGrow: 1,
              }}
            >
              <Grid container spacing={2} className="CONTAINER-MAIN">
                <Grid item xs={12} sm={12} className="CONTAINER-TOP">
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} className="SELF">
                      <StyledPaperItem
                        sx={{
                          paddingX: 2,
                        }}
                      >
                        <Box sx={{ color: '#B0E0E6' }}>
                          <Typography variant="h6">
                            Games seeded by{' '}
                            <Typography
                              variant="h6"
                              display={'inline'}
                              fontWeight={600}
                              color="info.main"
                            >
                              {username}
                            </Typography>
                          </Typography>
                          <Typography variant="h6">
                            {currentAllTimeToggle
                              ? selfTotalSeeds
                              : selfLiveSeeds}
                          </Typography>
                        </Box>
                      </StyledPaperItem>
                    </Grid>
                    <Grid item xs={12} sm={12} className="TOTALS">
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={6} className="TOTAL-GAMES">
                          <StyledPaperItem
                            sx={{
                              paddingX: 2,
                            }}
                          >
                            <Box sx={{ color: '#B0E0E6' }}>
                              <Typography variant="h6">
                                Games seeded by community
                              </Typography>
                              <Typography variant="h6">
                                {totalMatches}
                              </Typography>
                            </Box>
                          </StyledPaperItem>
                        </Grid>
                        <Grid item xs={6} sm={6} className="TOTAL-PLAYERS">
                          <StyledPaperItem
                            sx={{
                              paddingX: 2,
                            }}
                          >
                            <Box sx={{ color: '#B0E0E6' }}>
                              <Typography variant="h6">
                                Total unique seeders
                              </Typography>
                              <Typography variant="h6">
                                {searchPoolData.uniqueSeeders}
                              </Typography>
                            </Box>
                          </StyledPaperItem>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} sm={12} className="BAR-CHART">
                      <StyledPaperItem
                        sx={{
                          paddingX: 2,
                          paddingBottom: 4,
                        }}
                      >
                        <Typography variant="h6" mb={2}>
                          Games currently available by MMR range
                        </Typography>
                        {searchPoolData ? (
                          <SearchPoolStackedBar
                            searchPoolData={searchPoolData}
                            totalMatches={totalMatches}
                          />
                        ) : null}
                      </StyledPaperItem>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={close} fullWidth>
            CLOSE
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
